import type { CalculatorInputValues } from '../calculators/types';
import {
  buildRoasScenarios,
  calculateRoasDecision,
  describeRoasDecision,
  type RoasDecisionInput,
} from '../calculators/roasDecision';
import {
  DecisionCardGrid,
  DecisionComparisonTable,
  DecisionInsight,
} from './DecisionSupport';

const currencyFormatter = new Intl.NumberFormat('ko-KR', { maximumFractionDigits: 0 });
const numberFormatter = new Intl.NumberFormat('ko-KR', { maximumFractionDigits: 1 });

function numberValue(values: CalculatorInputValues, key: string): number {
  const value = values[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : 0;
}

function optionalNumber(values: CalculatorInputValues, key: string): number | undefined {
  const value = values[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined;
}

function toRoasInput(values: CalculatorInputValues): RoasDecisionInput {
  return {
    adSpend: numberValue(values, 'adSpend'),
    revenue: numberValue(values, 'revenue'),
    marginRate: optionalNumber(values, 'marginRate'),
    targetRoas: optionalNumber(values, 'targetRoas'),
  };
}

function won(value: number | null): string {
  return value === null ? '계산 불가' : `${currencyFormatter.format(Math.round(value))}원`;
}

function percent(value: number | null): string {
  return value === null ? '계산 불가' : `${numberFormatter.format(value)}%`;
}

export function RoasDecisionPanel({ values }: { values: CalculatorInputValues }) {
  const input = toRoasInput(values);
  const result = calculateRoasDecision(input);
  const hasMarginRate = typeof input.marginRate === 'number' && input.marginRate > 0;
  const hasTargetRoas = typeof input.targetRoas === 'number' && input.targetRoas > 0;

  const cards = [
    {
      label: '손익분기 ROAS',
      value: hasMarginRate ? percent(result.breakEvenRoas) : '마진율 미입력',
      detail: hasMarginRate
        ? `광고비 제외 마진율 ${percent(result.marginRate)} 기준으로 광고비를 회수하기 위한 최소 ROAS입니다.`
        : '광고비 제외 마진율을 입력하면 광고 손익분기 ROAS를 계산합니다.',
    },
    {
      label: '광고비 차감 후 추정 손익',
      value: hasMarginRate ? won(result.estimatedProfitAfterAd) : '마진율 미입력',
      detail: hasMarginRate
        ? `현재 광고 매출에서 마진율을 적용한 뒤 광고비 ${won(result.adSpend)}를 차감한 값입니다.`
        : '마진율 입력 시 현재 광고 성과가 실제로 이익인지 손실인지 추정합니다.',
    },
    {
      label: '목표 ROAS 필요 매출',
      value: hasTargetRoas ? won(result.targetRevenue) : '목표 ROAS 미입력',
      detail: hasTargetRoas && result.revenueGapToTarget !== null
        ? result.revenueGapToTarget > 0
          ? `현재 광고 매출보다 ${won(result.revenueGapToTarget)}이 더 필요합니다.`
          : `현재 광고 매출이 목표 기준을 ${won(Math.abs(result.revenueGapToTarget))} 상회합니다.`
        : '목표 ROAS를 입력하면 현재 광고비에서 필요한 광고 매출을 역산합니다.',
    },
    {
      label: '목표 ROAS 기준 최대 광고비',
      value: hasTargetRoas ? won(result.maxAdSpendAtTarget) : '목표 ROAS 미입력',
      detail: hasTargetRoas && result.adSpendHeadroomToTarget !== null
        ? result.adSpendHeadroomToTarget >= 0
          ? `현재 매출을 유지한다면 광고비를 ${won(result.adSpendHeadroomToTarget)}까지 추가할 여지가 있습니다.`
          : `현재 광고비가 목표 기준 최대치보다 ${won(Math.abs(result.adSpendHeadroomToTarget))} 높습니다.`
        : '현재 광고 매출에서 목표 ROAS를 지키기 위한 광고비 상한입니다.',
    },
  ];

  const scenarioColumns = ['ROAS', '매출 대비 광고비'];
  if (hasMarginRate) scenarioColumns.push('광고비 차감 후 추정 손익');

  const scenarios = buildRoasScenarios(input).map((scenario) => ({
    id: scenario.id,
    label: scenario.label,
    description: scenario.description,
    values: [
      percent(scenario.result.roas),
      percent(scenario.result.adCostRate),
      ...(hasMarginRate ? [won(scenario.result.estimatedProfitAfterAd)] : []),
    ],
  }));

  return (
    <section className="decision-support" aria-labelledby="roas-decision-title">
      <div className="decision-support-heading">
        <p className="eyebrow">DECISION SUPPORT</p>
        <h2 id="roas-decision-title">ROAS 광고 손익 의사결정</h2>
        <p>ROAS 숫자만 보는 대신 마진 기준 손익분기점과 목표 성과, 광고비·매출 악화 시나리오를 함께 비교합니다.</p>
      </div>

      <DecisionInsight title="현재 광고 성과 해석">
        <p>{describeRoasDecision(result)}</p>
        <p className="decision-caveat">
          광고 매출은 사용자가 입력한 귀속 매출을 그대로 사용합니다. 광고비 제외 마진율에는 상품원가·수수료·배송비 등 광고비 이전에 차감할 비용을 사용자가 반영해야 하며, 이 결과는 세금·환불·반품·LTV·재구매를 추정하지 않는 운영 의사결정용 값입니다.
        </p>
      </DecisionInsight>

      <DecisionCardGrid
        title="손익분기와 목표 기준"
        description="선택 입력을 추가하면 현재 ROAS가 손익분기점과 목표 수준을 충족하는지 판단할 수 있습니다."
        items={cards}
      />

      <DecisionComparisonTable
        title="광고 성과 하방 시나리오"
        description="광고 매출 감소와 광고비 상승이 ROAS와 추정 손익에 미치는 영향을 같은 기준으로 비교합니다."
        columns={scenarioColumns}
        rows={scenarios}
      />
    </section>
  );
}
